import { defineStore } from 'pinia';
import isMobile from '../../utils/is-mobile';
import { useCompositionStore } from '@/store-pinia/composition';
import store from '@/store';
import { GETTER_ACTIVE_PRINT_SIDE_PRINT_STYLE } from '@/store/getters';
import { useRouterStore } from '@/store-pinia/router';
import {
  ELEMENT_TYPE_DESIGN,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_UPLOAD
} from '@/plugins/composition-renderer/constants';
import {
  PRINT_STYLE_CUP_PRINT,
  PRINT_STYLE_DTG,
  PRINT_STYLE_DTF,
  PRINT_STYLE_FLEX,
  PRINT_STYLE_FLOCK,
  PRINT_STYLE_SUBLIMATION
} from '../../utils/constants';
import uploadMaxSize from '@/store-pinia/control-layer/watcher/uploadMaxSize';
import allowElementToResizeOnImageDimensions from '@/store-pinia/control-layer/guard/allowElementToResizeOnImageDimensions';
import { BASE_LAYER_ZINDEX } from '@/plugins/composition-renderer/plugin';
import {
  TYPE_RESTART,
  TYPE_VETO
} from '@/store-pinia/control-layer/guard-template';
import fontMinimumSize from '@/store-pinia/control-layer/guard/fontMinimumSize';
import { useColorStore } from '@/store-pinia/colors';

export const useControlLayerStore = defineStore('control-layer', {
  state: () => ({
    selectedElementUids: [],
    elementWatcherFactories: [uploadMaxSize],
    elementActiveWatchers: {},
    guards: [allowElementToResizeOnImageDimensions, fontMinimumSize],
    elementAllowedToResizeByImageDimensions: new Set(),
    elementAllowedToResizeByDPI: new Set(),
    configData: {
      maxElementsPerSide: 400,
      baseLayerZIndex: BASE_LAYER_ZINDEX,
      elementDefaults: {
        width: 50,
        height: 30,
        top: 10,
        left: 25,
        rotation: 0
      },
      textDefaults: {
        font: 26, // Rounded Pro
        color: 1, // Black
        align: 'center',
        effect: 1, // No text effect
        effectColor: 13 // Red
      },
      minHeightInMm: {
        [PRINT_STYLE_CUP_PRINT]: 1,
        [PRINT_STYLE_DTG]: 1,
        [PRINT_STYLE_DTF]: 1,
        [PRINT_STYLE_FLEX]: 18,
        [PRINT_STYLE_FLOCK]: 18,
        [PRINT_STYLE_SUBLIMATION]: 1
      },
      uploadMinDpi: 150
    },
    printStyleColorHistory: {}
  }),
  getters: {
    activeElement() {
      const compositionStore = useCompositionStore();
      return compositionStore.getElementByUid(this.selectedElementUids[0]);
    },
    config(state) {
      return {
        minLayerLevel: 1,
        maxLayerLevel: state.configData.maxElementsPerSide,
        baseLayerZIndex: state.configData.baseLayerZIndex,
        controlLayerZIndex:
          state.configData.baseLayerZIndex +
          state.configData.maxElementsPerSide,
        activeLayerZIndex:
          state.configData.baseLayerZIndex +
          state.configData.maxElementsPerSide * 2,
        textDefaults: state.configData.textDefaults,
        elementDefaults: state.configData.elementDefaults
      };
    },
    activePrintSide() {
      return store.state.activePrintSide;
    }
  },
  actions: {
    updateElements(changesets, iteration = 0) {
      if (iteration > 24) {
        return;
      }

      try {
        this.guards.forEach(guard => guard(changesets));
      } catch (guardEffect) {
        if (TYPE_VETO === guardEffect.type) {
          return;
        }
        if (TYPE_RESTART === guardEffect.type) {
          return this.updateElements(guardEffect.changesets, iteration + 1);
        }
      }

      const compositionStore = useCompositionStore();
      changesets.forEach(({ uid, changeset }) =>
        compositionStore.updateElement(uid, changeset)
      );
    },
    selectElement(uid) {
      if (this.selectedElementUids.includes(uid)) {
        return;
      }

      // Only allow for single selection
      this.selectedElementUids = [uid];

      // This would add elements to the selection for grouping
      // this.selectedElementUids.push(uid);

      if (!isMobile()) {
        this.editElement(uid);
      }
    },
    editElement(uid) {
      const store = useCompositionStore();
      const element = store.getElementByUid(uid);

      const routerStore = useRouterStore();

      switch (element.type) {
        case 'text': {
          routerStore.currentRoute = 'edit-text';
          break;
        }
        case 'design': {
          routerStore.currentRoute = 'edit-design';
          break;
        }
        case 'upload': {
          routerStore.currentRoute = 'edit-upload';
          break;
        }
      }
    },
    unselectAllElements() {
      this.selectedElementUids = [];

      const routerStore = useRouterStore();
      routerStore.currentRoute = null;
    },
    elementToBack(uid) {
      const compositionStore = useCompositionStore();
      const side = compositionStore.getPrintSideNameOfElement(uid);
      const printPlate = compositionStore.getPrintSideContent(side);
      const newLayerLevel =
        printPlate.content.reduce(
          (prev, element) => Math.min(prev, element.layerLevel),
          this.config.maxLayerLevel
        ) - 1;

      compositionStore.updateElement(uid, {
        layerLevel: newLayerLevel
      });

      if (newLayerLevel < this.config.minLayerLevel) {
        this.reCalculateZindex(side);
      }
    },
    elementToFront(uid) {
      const compositionStore = useCompositionStore();
      const side = compositionStore.getPrintSideNameOfElement(uid);
      const printPlate = compositionStore.getPrintSideContent(side);
      const newLayerLevel =
        printPlate.content.reduce(
          (prev, element) => Math.max(prev, element.layerLevel),
          this.config.minLayerLevel
        ) + 1;

      compositionStore.updateElement(uid, {
        layerLevel: newLayerLevel
      });

      if (newLayerLevel > this.config.maxLayerLevel) {
        this.reCalculateZindex(side);
      }
    },
    reCalculateZindex(side) {
      //TODO potentially move to a guard?
      const compositionStore = useCompositionStore();
      const printPlate = compositionStore.getPrintSideContent(side);

      let zIndex = Math.floor(
        this.config.minLayerLevel +
          (this.config.maxLayerLevel -
            this.config.minLayerLevel -
            printPlate.content.length) /
            4
      );

      printPlate.content
        .map(e => ({
          layerLevel: e.layerLevel,
          uid: e.uid,
          newLayerLevel: null
        }))
        .sort((a, b) => a.layerLevel - b.layerLevel)
        .map(e => ({ ...e, newLayerLevel: zIndex++ }))
        .filter(e => e.layerLevel !== e.newLayerLevel)
        .map(e =>
          compositionStore.updateElement(e.uid, {
            layerLevel: e.newLayerLevel
          })
        );
    },

    addElementOnlyForInternalUse(element) {
      const uid = window.crypto.randomUUID();
      const compositionStore = useCompositionStore();

      const currentElements = compositionStore.getPrintSideContent(
        this.activePrintSide
      ).content;

      const offset = (currentElements.length % 10) * 5;

      const template = {
        uid,
        layerLevel: this.config.minLayerLevel,
        ...this.config.elementDefaults,
        top: this.config.elementDefaults.top + offset,
        left: this.config.elementDefaults.left + offset
      };

      compositionStore.addElement(this.activePrintSide, {
        ...element,
        ...template
      });

      this.elementAllowedToResizeByImageDimensions.add(uid);
      this.elementToFront(uid);
      this.selectElement(uid);
      this.recalculateWatchers();

      return uid;
    },
    addTextElement(text) {
      const colorStore = useColorStore();
      const color = colorStore.findColorForPrintStyle(
        this.config.textDefaults.color,
        store.getters[GETTER_ACTIVE_PRINT_SIDE_PRINT_STYLE]
      );

      const element = {
        type: ELEMENT_TYPE_TEXT,
        text: text,
        font: this.config.textDefaults.font,
        color,
        effect: this.config.textDefaults.effect,
        effectColor: this.config.textDefaults.effectColor,
        effectConfig: {},
        align: this.config.textDefaults.align
      };

      return this.addElementOnlyForInternalUse(element);
    },
    addDesignElement(designId) {
      const element = {
        type: ELEMENT_TYPE_DESIGN,
        id: designId
      };

      return this.addElementOnlyForInternalUse(element);
    },
    addUploadElement(srcFile) {
      const element = {
        type: ELEMENT_TYPE_UPLOAD,
        srcFile: srcFile,
        bgRemoval: false,
        effect: 0,
        config: {}
      };

      const uid = this.addElementOnlyForInternalUse(element);

      this.elementAllowedToResizeByDPI.add(uid);

      return uid;
    },
    getWatchers(uid) {
      if (
        !Object.prototype.hasOwnProperty.call(this.elementActiveWatchers, uid)
      ) {
        return [];
      }
      return this.elementActiveWatchers[uid];
    },
    recalculateWatchers() {
      const printPlates = useCompositionStore().composition.printPlates;
      for (const side in printPlates) {
        printPlates[side].content.forEach(element => {
          this.elementActiveWatchers[element.uid] = this.elementWatcherFactories
            .map(factory =>
              factory({
                element,
                printPlate: printPlates[side],
                printSide: side
              })
            )
            .filter(watcher => null !== watcher);
        });
      }
    },

    async updateSidePrintStyle(side, printStyle) {
      const compositionStore = useCompositionStore();
      const colorStore = useColorStore();
      const printPlate = compositionStore.getPrintSideContent(side);
      const currentPrintStyle = printPlate.printStyle;
      await colorStore.loadFontColors();

      let newContent = JSON.parse(JSON.stringify(printPlate.content));

      newContent = newContent.filter(element =>
        store.getters.getElementCapabilities(element).includes(printStyle)
      );

      newContent.forEach(element => {
        if (ELEMENT_TYPE_TEXT === element.type) {
          const historyObj = this.printStyleColorHistory;
          if (!historyObj[element.uid]) {
            historyObj[element.uid] = {};
          }

          historyObj[element.uid][currentPrintStyle] = element.color;

          const color = colorStore.color(element.color);

          if (!color.capabilities.includes(printStyle)) {
            const savedColor = historyObj[element.uid][printStyle];
            if (savedColor !== undefined) {
              element.color = savedColor;
            } else {
              element.color = colorStore.findColorForPrintStyle(
                color.id,
                printStyle
              );
            }
          }
        }
      });

      compositionStore.updateSideContent(side, newContent);
      compositionStore.updateSidePrintStyle(side, printStyle);

      this.updateElements([
        newContent.map(el => ({
          uid: el.uid,
          changeset: {}
        }))
      ]);
    },
    clearPrintStyleColorHistory() {
      this.printStyleColorHistory = {};
    }
  }
});
